// HMRC rate fields and currency helpers shared by the job forms.
import type { HmrcAmounts, HmrcCity } from "../api/types";
import { money } from "./format";

/** The job's rate fields, in the order GOV.UK lists them. */
export const HMRC_FIELDS = [
  ["lunch_rate", "lunch", "Lunch"],
  ["dinner_rate", "dinner", "Dinner"],
  ["drink_rate", "drink", "Drinks"],
  ["hotel_rate", "hotel", "Hotel to office"],
] as const satisfies readonly (readonly [string, keyof HmrcAmounts, string])[];

export type HmrcRateKey = (typeof HMRC_FIELDS)[number][0];

/** "€" for EUR, "£" for pounds (null), else the code itself. */
export function currencySymbol(code: string | null | undefined): string {
  if (!code) return "£";
  try {
    const parts = new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: code,
      currencyDisplay: "narrowSymbol",
    }).formatToParts(0);
    return parts.find((p) => p.type === "currency")?.value ?? code;
  } catch {
    return code;
  }
}

export function localMoney(n: number, code: string | null | undefined, name = code ?? "") {
  if (!code) return money(n);
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: code }).format(n);
  } catch {
    return `${n.toFixed(2)} ${name}`;
  }
}

/**
 * What choosing a city does to the job's HMRC fields: the GOV.UK figures in local currency, which
 * each day then converts at its own exchange rate. With no city, or a currency that can't be
 * converted, the rates go back to 0 in pounds for you to type.
 */
export function cityFields(c: HmrcCity | null) {
  const usable = c?.currency ? c : null;
  return {
    hmrc_city: c?.city ?? "",
    hmrc_currency: usable?.currency ?? "",
    ...(Object.fromEntries(HMRC_FIELDS.map(([k, g]) => [k, String(usable ? usable.local[g] : 0)])) as Record<
      HmrcRateKey,
      string
    >),
  };
}

/** "São Paulo" and "sao paulo" are the same, for searching. */
const fold = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** Cities whose name or country has every word typed, in any order; names starting with it first. */
export function matchCities(cities: HmrcCity[], query: string): HmrcCity[] {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (!words.length) return cities;
  const q = words.join(" ");
  const hits = cities.filter((c) => {
    const text = fold(`${c.city} ${c.country}`);
    return words.every((w) => text.includes(w));
  });
  const starts = (c: HmrcCity) => (fold(c.city).startsWith(q) ? 0 : 1);
  return hits.sort((a, b) => starts(a) - starts(b));
}
